import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { formatINR, formatDate, exportToCSV } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Download, Trash2, RefreshCw, Eye, Calendar, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLeads = () => {
  const { getToken } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    
    try {
      await axios.delete(`${API}/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleExport = () => {
    if (leads.length === 0) {
      toast.error("No leads to export");
      return;
    }
    exportToCSV(leads, `wedding-leads-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Export downloaded");
  };

  if (loading) {
    return (
      <AdminLayout title="Leads">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Leads">
      <div className="space-y-6" data-testid="leads-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            {leads.length} lead{leads.length !== 1 ? 's' : ''} captured
          </p>
          <Button
            onClick={handleExport}
            variant="outline"
            data-testid="export-leads-btn"
            disabled={leads.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Leads Table */}
        {leads.length > 0 ? (
          <div className="bg-white rounded-2xl border border-border/40 shadow-elegant overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Event Date</th>
                    <th>Estimated Budget</th>
                    <th>Submitted</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                      <td>
                        <div>
                          <p className="font-medium text-[#2D2D2D]">{lead.name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {lead.event_date ? (
                          <span className="flex items-center gap-1 text-sm">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            {lead.event_date}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">Not specified</span>
                        )}
                      </td>
                      <td>
                        <span className="font-semibold text-[#B08968]">
                          {formatINR(lead.estimate_snapshot?.grand_total || 0)}
                        </span>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setSelectedLead(lead)}
                            data-testid={`view-lead-${lead.id}`}
                            className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(lead.id)}
                            data-testid={`delete-lead-${lead.id}`}
                            className="p-2 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-border/40 p-12 text-center">
            <p className="text-muted-foreground">
              No leads yet. They will appear here when customers submit estimates.
            </p>
          </div>
        )}
      </div>

      {/* Lead Details Modal */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl" data-testid="lead-details-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Lead Details</DialogTitle>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Name</p>
                  <p className="font-medium">{selectedLead.name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Phone</p>
                  <p className="font-medium">{selectedLead.phone}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Email</p>
                  <p className="font-medium">{selectedLead.email}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Event Date</p>
                  <p className="font-medium">{selectedLead.event_date || "Not specified"}</p>
                </div>
              </div>
              
              {selectedLead.notes && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm bg-secondary/50 p-3 rounded-lg">{selectedLead.notes}</p>
                </div>
              )}
              
              {/* Estimate Breakdown */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-4">Estimate Breakdown</h3>
                
                {selectedLead.estimate_snapshot && (
                  <div className="space-y-4 text-sm">
                    {/* Venue */}
                    <div className="bg-secondary/30 p-4 rounded-lg">
                      <p className="font-medium text-[#B08968] mb-2">Venue</p>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Duration:</span>
                        <span>{selectedLead.inputs_snapshot?.duration_hours || 48} hours</span>
                        <span className="text-muted-foreground">Advance:</span>
                        <span>{selectedLead.inputs_snapshot?.advance_payment || 0}%</span>
                        <span className="text-muted-foreground">Guests:</span>
                        <span>{selectedLead.inputs_snapshot?.guest_count || 0} pax</span>
                        <span className="text-muted-foreground font-medium">Venue Total:</span>
                        <span className="font-medium">{formatINR(selectedLead.estimate_snapshot.venue?.total)}</span>
                      </div>
                    </div>
                    
                    {/* Food */}
                    <div className="bg-secondary/30 p-4 rounded-lg">
                      <p className="font-medium text-[#B08968] mb-2">Food</p>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Tier:</span>
                        <span>{selectedLead.estimate_snapshot.food?.tier_name}</span>
                        <span className="text-muted-foreground font-medium">Food Total:</span>
                        <span className="font-medium">{formatINR(selectedLead.estimate_snapshot.food?.total)}</span>
                      </div>
                    </div>
                    
                    {/* Decor */}
                    {selectedLead.estimate_snapshot.decor?.items?.length > 0 && (
                      <div className="bg-secondary/30 p-4 rounded-lg">
                        <p className="font-medium text-[#B08968] mb-2">
                          Decor ({selectedLead.estimate_snapshot.decor.items.length} items)
                        </p>
                        <div className="max-h-32 overflow-y-auto space-y-1 mb-2">
                          {selectedLead.estimate_snapshot.decor.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{item.name}</span>
                              <span>{formatINR(item.tariff)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="font-medium">Decor Total:</span>
                          <span className="font-medium">{formatINR(selectedLead.estimate_snapshot.decor?.total)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Grand Total */}
                    <div className="bg-[#B08968]/10 p-4 rounded-lg flex justify-between items-center">
                      <span className="text-lg font-medium">Grand Total</span>
                      <span className="text-2xl font-bold text-[#B08968]">
                        {formatINR(selectedLead.estimate_snapshot.grand_total)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                Submitted: {formatDate(selectedLead.created_at)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminLeads;
